import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { OWNER_TIMEZONE, formatPrice } from '@diana/shared'
import { api, type StudioDashboard } from '../../lib/api.js'
import { useSession } from '../../lib/auth.js'
import { Column, Main, TopTitle } from '../../classroom/Shell.js'
import { Dot, ErrorLine, SearchBox, Skeleton, Stat, StatGrid, type Tone } from '../../classroom/ui.js'
import { PlanClassLine } from './PlanSection.js'

/** „w październiku” — Polish months in the locative. */
const IN_MONTH = ['styczniu', 'lutym', 'marcu', 'kwietniu', 'maju', 'czerwcu', 'lipcu', 'sierpniu', 'wrześniu', 'październiku', 'listopadzie', 'grudniu']

const plural = (n: number, one: string, few: string, many: string) =>
  n === 1 ? one : n % 10 >= 2 && n % 10 <= 4 && !(n % 100 >= 12 && n % 100 <= 14) ? few : many

/**
 * Studio → Start (Studio-Dash.png): three numbers, today's lessons with the
 * requests waiting for her answer, and a short „Do zrobienia” list built
 * from what the books and the courses already know. The site photographs
 * moved to „Strona i treści”.
 *
 * The mockup's „Sprzedaż” figure waits for the shop (decision 2a); until
 * then the second number is what the lessons earned this month.
 */
export function DashboardPage() {
  const navigate = useNavigate()
  const { data: session } = useSession()
  const [query, setQuery] = useState('')
  const { data, isPending, isError } = useQuery({ queryKey: ['studio', 'dashboard'], queryFn: api.studio.dashboard })
  const first = (session?.user.name ?? 'Diana').split(/\s+/)[0]
  const month = IN_MONTH[DateTime.now().setZone(OWNER_TIMEZONE).month - 1]

  return (
    <Column
      density="studio"
      top={
        <>
          <TopTitle studio>Strona główna</TopTitle>
          <SearchBox
            className="ml-auto"
            width={280}
            value={query}
            onChange={setQuery}
            onSubmit={() => navigate(query.trim() ? `/studio/klienci?q=${encodeURIComponent(query.trim())}` : '/studio/klienci')}
            placeholder="Szukaj ucznia…"
            label="Szukaj ucznia"
          />
        </>
      }
    >
      <Main density="studio">
        <section className="flex flex-col gap-6">
          <h1 className="cl-title m-0">Cześć, {first}</h1>
          {isPending ? (
            <Skeleton lines={2} />
          ) : isError || !data ? (
            <ErrorLine>Nie udało się wczytać. Odśwież stronę.</ErrorLine>
          ) : (
            <StatGrid>
              <Stat label="Lekcje dziś" value={data.lessons.length} to="/studio/kalendarz" />
              <Stat
                label={`Zarobione w ${month}`}
                value={formatPrice(data.earnedThisMonthGrosz)}
                hint={`${data.lessonsThisMonth} ${plural(data.lessonsThisMonth, 'odbyta lekcja', 'odbyte lekcje', 'odbytych lekcji')}`}
                to="/studio/rozliczenia"
              />
              <Stat label="Nowe wiadomości" value={data.newLeads} to="/studio/wiadomosci" />
            </StatGrid>
          )}
        </section>

        {data && (
          <section className="grid items-start gap-10 desk:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] desk:gap-12">
            <Today data={data} />
            <Todo data={data} />
          </section>
        )}
      </Main>
    </Column>
  )
}

function Today({ data }: { data: StudioDashboard }) {
  const queryClient = useQueryClient()
  const answer = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'confirmed' | 'cancelled' }) => api.studio.updateBooking(id, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['studio'] }),
  })
  const time = (iso: string) => DateTime.fromISO(iso).setZone(OWNER_TIMEZONE).toFormat('HH:mm')

  return (
    <div className="flex flex-col">
      <div className="flex items-baseline pb-3">
        <h2 className="cl-subheading m-0">Dziś</h2>
        <Link to="/studio/kalendarz" className="ml-auto text-class-gloss font-semibold">
          Kalendarz ›
        </Link>
      </div>
      {data.lessons.length === 0 ? (
        <p className="m-0 border-t border-class-line pt-4 text-class-ink-soft">Dziś nie masz lekcji.</p>
      ) : (
        <div className="flex flex-col [&>*]:border-t [&>*]:border-class-line [&>*:last-child]:border-b">
          {data.lessons.map((l) => {
            const pending = l.status === 'pending'
            return (
              <div key={l.id} className="flex min-h-[60px] flex-wrap items-center gap-x-4 gap-y-2 py-2">
                <span className="w-[52px] font-semibold">{time(l.startsAt)}</span>
                <span className="min-w-0 flex-1 leading-snug">
                  <span className="block font-semibold">
                    {l.clientId ? <Link to={`/studio/klienci/${l.clientId}`} className="!text-class-ink">{l.who}</Link> : l.who}
                    {l.what && <span className="font-normal text-class-ink-soft"> · {l.what}</span>}
                  </span>
                  <span className="text-class-gloss text-class-ink-soft">
                    {pending ? 'Prośba ze strony' : l.fromSite ? 'Zapis ze strony' : 'Lekcja indywidualna'}
                    {l.goals && pending ? ` · ${l.goals}` : ''}
                  </span>
                  {l.plan.map((p, i) => (
                    <PlanClassLine key={`${p.planId}-${p.lessonSlug ?? i}`} item={p} clientId={l.clientId} />
                  ))}
                </span>
                {pending ? (
                  <span className="flex items-center gap-1">
                    <button
                      type="button"
                      disabled={answer.isPending}
                      onClick={() => answer.mutate({ id: l.id, status: 'confirmed' })}
                      className="cl-btn cl-btn-primary min-h-10 px-[18px] text-class-gloss"
                    >
                      Potwierdź
                    </button>
                    <button
                      type="button"
                      disabled={answer.isPending}
                      onClick={() => window.confirm(`Odrzucić prośbę: ${l.who}, ${time(l.startsAt)}?`) && answer.mutate({ id: l.id, status: 'cancelled' })}
                      className="cl-link-quiet min-h-10 px-1.5 text-class-gloss"
                    >
                      Odrzuć
                    </button>
                  </span>
                ) : l.meetingUrl ? (
                  <a href={l.meetingUrl} target="_blank" rel="noreferrer" className="text-class-gloss font-semibold">
                    Dołącz
                  </a>
                ) : null}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function Todo({ data }: { data: StudioDashboard }) {
  const items: { tone: Tone; text: string; to: string }[] = []
  if (data.pendingRequests > 0)
    items.push({
      tone: 'wait',
      text: `${data.pendingRequests} ${plural(data.pendingRequests, 'prośba', 'prośby', 'próśb')} o termin`,
      to: '/studio/kalendarz',
    })
  if (data.newLeads > 0)
    items.push({
      tone: 'neutral',
      text: `Odpowiedz na ${data.newLeads} ${plural(data.newLeads, 'wiadomość', 'wiadomości', 'wiadomości')}`,
      to: '/studio/wiadomosci',
    })
  for (const c of data.courses.filter((c) => c.published && c.missingRecordings > 0))
    items.push({
      tone: 'bad',
      text: `${c.title}: brakuje nagrań w ${c.missingRecordings} ${plural(c.missingRecordings, 'lekcji', 'lekcjach', 'lekcjach')}`,
      to: '/studio/kursy',
    })
  if (data.clientsOwing > 0)
    items.push({
      tone: 'wait',
      text: `${data.clientsOwing} ${plural(data.clientsOwing, 'osoba', 'osoby', 'osób')} do zapłaty`,
      to: '/studio/rozliczenia',
    })
  for (const c of data.courses.filter((c) => !c.published).slice(0, 3))
    items.push({ tone: 'neutral', text: `${c.title} — przeczytaj i opublikuj`, to: '/studio/kursy' })

  return (
    <div className="flex flex-col">
      <div className="flex items-baseline pb-3">
        <h2 className="cl-subheading m-0">Do zrobienia</h2>
      </div>
      {items.length === 0 ? (
        <p className="m-0 border-t border-class-line pt-4 text-class-ink-soft">Wszystko zrobione.</p>
      ) : (
        <div className="flex flex-col [&>*]:border-t [&>*]:border-class-line [&>*:last-child]:border-b">
          {items.map((it) => (
            <Link key={it.text} to={it.to} className="flex min-h-[60px] items-center gap-4 !text-class-ink hover:bg-class-field-soft/50">
              <Dot tone={it.tone} />
              <span className="flex-1 text-class-gloss">{it.text}</span>
              <span className="text-class-gloss font-semibold text-class-sky-700">›</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
